// apps/web/src/pages/pcp/pendencia-cancelamento-nfe.tsx — Fase 17.1, Plano 28 (L-63).
// Destaque da pendência CANCELAMENTO_NFE na Central de Pendências Operacionais: o texto "NF de remessa cancelada na Omie"
// e o caminho até o estorno do recebimento (`/remessas?estornar=<documentoFiscalId>`). A pendência guarda o documento
// fiscal da nota em `entidadeId`. Quem estorna é quem tem `remessas:estornar`; a tela de Remessas orienta quem não tem.
import type { ReactElement } from 'react';
import type { PendenciaOperacionalRow } from '@pieltec/contracts';

export const TEXTO_NF_CANCELADA_NA_OMIE = 'NF de remessa cancelada na Omie';
export const ROTULO_LINK_ESTORNAR_RECEBIMENTO = 'Estornar recebimento';
/** Assumir, atribuir, reatribuir e resolver exigem esta permissão na API; a página só oferece as ações a quem a tem. */
export const PERMISSAO_AGIR_NA_PENDENCIA = 'remessas:gerenciar';

type PendenciaDoCancelamento = Pick<PendenciaOperacionalRow, 'tipo' | 'estado' | 'entidadeTipo' | 'entidadeId'>;

export function ehCancelamentoDeNfe(pendencia: Pick<PendenciaOperacionalRow, 'tipo'>): boolean {
  return pendencia.tipo === 'CANCELAMENTO_NFE';
}

/** Link da pendência até o estorno do recebimento; `null` quando não há o que estornar (outro tipo ou já resolvida). */
export function linkDoEstornoDaPendencia(pendencia: PendenciaDoCancelamento): string | null {
  if (!ehCancelamentoDeNfe(pendencia) || pendencia.entidadeTipo !== 'NFE' || pendencia.estado === 'RESOLVIDA') return null;
  return `/remessas?estornar=${encodeURIComponent(pendencia.entidadeId)}`;
}

/** A sessão pode agir na pendência (assumir, atribuir, resolver)? Fail-closed enquanto a sessão não carrega. */
export function podeAgirNaPendencia(permissoes: readonly string[] | null): boolean {
  return permissoes !== null && permissoes.includes(PERMISSAO_AGIR_NA_PENDENCIA);
}

interface DestaqueCancelamentoNfeProps {
  readonly pendencia: PendenciaDoCancelamento;
}

export function DestaqueCancelamentoNfe({ pendencia }: DestaqueCancelamentoNfeProps): ReactElement | null {
  if (!ehCancelamentoDeNfe(pendencia)) return null;
  const link = linkDoEstornoDaPendencia(pendencia);
  return (
    <div data-testid="pendencia-cancelamento-nfe" className="mt-1 space-y-0.5 font-normal">
      <p className="text-rose-800 font-semibold">{TEXTO_NF_CANCELADA_NA_OMIE}</p>
      {link !== null && (
        <a href={link} className="text-blue-700 underline font-medium">
          {ROTULO_LINK_ESTORNAR_RECEBIMENTO}
        </a>
      )}
    </div>
  );
}
